import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Heart, Bookmark, Flag, Pin, Lock, Shield, Trash2, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Loading } from "@/components/shared";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PostDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [comment, setComment] = useState("");
  const { data, isLoading, refetch } = useQuery({ queryKey: ["post", id], queryFn: async () => (await api.get(`/community/posts/${id}`)).data, retry: false });

  if (isLoading) return <DashboardLayout><Loading /></DashboardLayout>;
  if (!data) return <DashboardLayout><div className="py-16 text-center text-slate-500">Post not found or locked.</div></DashboardLayout>;

  const { post, comments } = data;
  const act = async (fn, msg) => { try { await fn(); if (msg) toast.success(msg); refetch(); } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); } };
  const submitComment = async () => {
    if (!comment.trim()) return;
    await act(() => api.post(`/community/posts/${id}/comments`, { body: comment }), "Comment added");
    setComment("");
  };
  const moderate = (action) => act(() => api.post(`/community/posts/${id}/moderate?action=${action}`), `Post ${action}`).then(() => { if (action === "delete") navigate(-1); });

  return (
    <DashboardLayout>
      <button onClick={() => navigate(-1)} className="mb-5 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-navy" data-testid="post-back"><ArrowLeft className="h-4 w-4" /> Back</button>
      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="post-detail">
        <p className="text-xs font-semibold uppercase tracking-wider text-emerald">{post.space_name}</p>
        <div className="mt-2 flex items-center gap-2">
          {post.pinned && <Pin className="h-4 w-4 text-emerald" />}
          {post.locked && <Lock className="h-4 w-4 text-slate-400" />}
          <h1 className="font-heading text-2xl font-bold text-navy">{post.title}</h1>
        </div>
        <p className="mt-1 text-sm text-slate-400">by {post.author_name}</p>
        <p className="mt-4 whitespace-pre-line leading-relaxed text-slate-700">{post.body}</p>
        <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
          <Button variant="outline" size="sm" onClick={() => act(() => api.post(`/community/posts/${id}/react`))} data-testid="react-btn" className="rounded-md"><Heart className="mr-1.5 h-4 w-4 text-red-500" /> {post.reactions}</Button>
          <Button variant="outline" size="sm" onClick={() => act(() => api.post(`/community/posts/${id}/save`), "Saved")} data-testid="save-post-btn" className="rounded-md"><Bookmark className={`mr-1.5 h-4 w-4 ${post.is_saved ? "fill-emerald text-emerald" : ""}`} /> Save</Button>
          <Button variant="outline" size="sm" onClick={() => act(() => api.post(`/community/posts/${id}/report`), "Reported")} data-testid="report-btn" className="rounded-md"><Flag className="mr-1.5 h-4 w-4" /> Report</Button>
          {user?.role === "admin" && (
            <div className="ml-auto flex gap-2">
              <Button variant="outline" size="sm" onClick={() => moderate("pin")} className="rounded-md text-emerald" data-testid="mod-pin"><Pin className="h-4 w-4" /></Button>
              <Button variant="outline" size="sm" onClick={() => moderate("lock")} className="rounded-md" data-testid="mod-lock"><Lock className="h-4 w-4" /></Button>
              <Button variant="outline" size="sm" onClick={() => moderate("hide")} className="rounded-md" data-testid="mod-hide"><EyeOff className="h-4 w-4" /></Button>
              <Button variant="outline" size="sm" onClick={() => moderate("delete")} className="rounded-md text-red-600" data-testid="mod-delete"><Trash2 className="h-4 w-4" /></Button>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6">
        <h2 className="font-heading text-lg font-semibold text-navy">{comments.length} comments</h2>
        {!post.locked && (
          <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment…" rows={3} data-testid="comment-input" />
            <div className="mt-3 flex justify-end"><Button onClick={submitComment} className="rounded-md bg-navy text-white hover:bg-navysurface" data-testid="comment-submit">Comment</Button></div>
          </div>
        )}
        <div className="mt-4 space-y-3">
          {comments.map((c) => (
            <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`comment-${c.id}`}>
              <p className="text-sm font-semibold text-navy">{c.author_name}</p>
              <p className="mt-1 whitespace-pre-line text-sm text-slate-700">{c.body}</p>
            </div>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}
